// FirebaseService.ts
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  updateProfile,
  signInWithPopup,
  onAuthStateChanged,
  User,
  sendPasswordResetEmail,
} from "firebase/auth";
import { auth, provider } from "config";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query as firestoreQuery,
  setDoc,
  updateDoc,
  where,
  Query,
  WhereFilterOp,
  limit as firestoreLimit,
  DocumentData,
  orderBy,
} from "firebase/firestore";
import { db, storage } from "config";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { FIRESTORE_KEYS } from "../keys";
import { getFriendlyErrorMessage } from "./error-mapping"; // Assuming errorMappings.ts is in the same folder
import { haversineDistance } from "reusable";

export class FirebaseService {
  // Helper method to clean data before sending to Firestore
  static cleanFirestoreData(data: any): any {
    if (data === null || data === undefined) {
      return null;
    }

    if (typeof data === 'number') {
      // Replace NaN, Infinity, and -Infinity with 0
      if (isNaN(data) || !isFinite(data)) {
        return 0;
      }
      return data;
    }

    if (typeof data === 'string' || typeof data === 'boolean') {
      return data;
    }

    if (Array.isArray(data)) {
      return data.map(item => this.cleanFirestoreData(item));
    }

    if (typeof data === 'object') {
      const cleaned: any = {};
      for (const key in data) {
        if (data.hasOwnProperty(key)) {
          cleaned[key] = this.cleanFirestoreData(data[key]);
        }
      }
      return cleaned;
    }

    return data;
  }

  static async createUserWithEmailAndPass(email: string, password: string) {
    try {
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        email,
        password
      );
      return userCredential.user;
    } catch (error) {
      return {
        message: getFriendlyErrorMessage(error),
        type: "error" as const,
      };
    }
  }

  static async signInWithEmailAndPass(email: string, password: string) {
    try {
      const userCredential = await signInWithEmailAndPassword(
        auth,
        email,
        password
      );
      return userCredential.user;
    } catch (error: any) {
      return {
        message: getFriendlyErrorMessage(error),
        type: "error" as const,
      };
    }
  }

  static async updateUserProfile(user: User, displayName: string) {
    try {
      await updateProfile(user, { displayName });
    } catch (error) {
      return {
        message: getFriendlyErrorMessage(error),
        type: "error" as const,
      };
    }
  }

  static async signInWithGoogle() {
    try {
      const result = await signInWithPopup(auth, provider);
      return result.user;
    } catch (error) {
      return {
        message: getFriendlyErrorMessage(error),
        type: "error" as const,
      };
    }
  }

  static async onAuthStateChanges(callback: (user: User | null) => void) {
    onAuthStateChanged(auth, callback);
  }

  static async setFirestoreDoc(
    collectionPath: string,
    docId: string,
    data: any
  ) {
    try {
      // Log the original data for debugging
      console.log('Setting Firestore doc:', { collectionPath, docId, data });
      
      // Validate and clean data before sending to Firestore
      const cleanedData = this.cleanFirestoreData(data);
      
      console.log('Cleaned data:', cleanedData);
      
      const docRef = doc(db, collectionPath, docId);
      await setDoc(docRef, cleanedData, { merge: true });
      
      console.log('Successfully set document');
      
    } catch (error) {
      console.error('Firebase setDoc error:', error);
      console.error('Collection:', collectionPath);
      console.error('Doc ID:', docId);
      console.error('Original data:', data);
      return {
        message: getFriendlyErrorMessage(error),
        type: "error" as const,
      };
    }
  }

  static async updateFirestoreDoc(
    collectionPath: string,
    docId: string,
    data: any
  ) {
    try {
      // Validate and clean data before sending to Firestore
      const cleanedData = this.cleanFirestoreData(data);
      const docRef = doc(db, collectionPath, docId);
      await updateDoc(docRef, cleanedData);
    } catch (error) {
      console.error('Firebase updateDoc error:', error);
      console.error('Data that caused error:', data);
      return {
        message: getFriendlyErrorMessage(error),
        type: "error" as const,
      };
    }
  }

  static async getFirestoreDoc(collectionPath: string, docId: string) {
    try {
      const docRef = doc(db, collectionPath, docId);
      const docSnap = await getDoc(docRef);
      if (!docSnap.exists()) {
        return {
          message: "Unauthorized Request.",
          type: "error" as const,
        };
      }
      return docSnap.data();
    } catch (error) {
      return {
        message: getFriendlyErrorMessage(error),
        type: "error" as const,
      };
    }
  }

  static async uploadFilesAndRetrieveURLs(userId: string, files: FileList) {
    try {
      // Check file sizes before uploading (2MB = 2 * 1024 * 1024 bytes)
      const MAX_FILE_SIZE = 2 * 1024 * 1024;
      
      // Check if any file is a PDF and exceeds size limit
      const oversizedPdfs = Array.from(files).filter(file => 
        file.type === 'application/pdf' && file.size > MAX_FILE_SIZE
      );
      
      if (oversizedPdfs.length > 0) {
        return {
          message: `PDF file size exceeds 2MB limit. Please compress your PDF and try again.`,
          type: "error" as const,
        };
      }

      const uploads = Array.from(files).map((file) => {
        const fileRef = ref(storage, `user_photos/${userId}/${file.name}`);
        return uploadBytes(fileRef, file).then((snapshot) =>
          getDownloadURL(snapshot.ref)
        );
      });
      return await Promise.all(uploads);
    } catch (error) {
      return {
        message: getFriendlyErrorMessage(error),
        type: "error" as const,
      };
    }
  }

  static async searchSummaries(term: string) {
    try {
      const profilesRef = collection(
        db,
        FIRESTORE_KEYS.CREATOR_PROFILES_SUMMARY
      );
      const searchQuery = firestoreQuery(
        profilesRef,
        where("searchTerms", "array-contains", term.toLowerCase()),
        orderBy("creationTime", "desc")
      );
      const querySnapshot = await getDocs(searchQuery);
      return querySnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
      return {
        message: getFriendlyErrorMessage(error),
        type: "error" as const,
      };
    }
  }

  //TODO : Improve this function, optimize, reduce fetching, caching
  static async searchByLocation<T>(
    collectionName: string,
    centerLat: number,
    centerLon: number,
    maxDistance: number, // Distance in kilometers
    start: number,
    end: number,
    conditions?: { field: string; operator: WhereFilterOp; value: any }[]
  ): Promise<T[] | { message: string; type: "error" }> {
    try {
      if (start > end || !centerLat || !centerLon) {
        return [];
      }

      const queryConstraints: any[] = [];
      conditions?.forEach((condition) => {
        queryConstraints.push(
          where(condition.field, condition.operator, condition.value)
        );
      });      // Optimized fetch with dynamic sizing based on actual needs
      // Reduced from fixed 20 to dynamic limit based on end parameter
      const dynamicLimit = Math.min(15, end + 5); // Max 15, or end + small buffer
      
      console.log(`FirebaseService.searchByLocation: Dynamic query - requested: ${end}, limit: ${dynamicLimit} (reduced from fixed 20)`);
      
      let query: Query = firestoreQuery(
        collection(db, collectionName),
        ...queryConstraints,
        orderBy("creationTime", "desc"),
        firestoreLimit(dynamicLimit) // Dynamic limit instead of fixed 20
      );
      const initialDocs = await getDocs(query);
      const documents = initialDocs.docs;

      if (documents.length === 0) {
        return []; // No documents at all
      }      // Filter documents based on Haversine distance
      const filteredDocuments = documents
        .map((doc) => {
          const data = doc.data() as T;
          const { lat, lng } =
            (data as any)?.location ||
            ({ latitude: 0, longitude: 0 } as unknown as {
              latitude: number;
              longitude: number;
            });
          const distance = haversineDistance(centerLat, centerLon, lat, lng);
          return { data, distance };
        })
        .filter(({ distance }) => distance <= maxDistance)
        .map(({ data }) => data);

      if (filteredDocuments.length === 0) {
        return []; // No documents within the specified distance
      }
      
      // Create a map to store unique documents by userId to prevent duplicates
      const uniqueDocMap = new Map();
      filteredDocuments.forEach(doc => {
        const uniqueId = (doc as any).userId || (doc as any).id;
        uniqueDocMap.set(uniqueId, doc);
      });

      // Convert map back to array
      const uniqueFilteredDocs = Array.from(uniqueDocMap.values());

      // Slice the results based on start and end
      const sliceStart = start - 1; // Convert to zero-based index for slicing
      const sliceEnd = Math.min(uniqueFilteredDocs.length, end); // Ensure not exceeding the actual number of documents

      return uniqueFilteredDocs.slice(sliceStart, sliceEnd);
    } catch (error) {
      return {
        message: getFriendlyErrorMessage(error),
        type: "error" as const,
      };
    }
  }
  //TODO : Improve this function, optimize, reduce fetching, caching
  static async fetchDataInRange<T>(
    collectionName: string,
    start: number,
    end: number,
    term?: string
  ) {
    try {
      if (start > end) {
        throw new Error("Start index cannot be greater than end index.");
      }

      if (term?.length) {
        return this.searchSummaries(term);
      }

      // Calculate how many documents we need to fetch with minimal buffer
      const fetchLimit = end;
      // Add small buffer only for duplicate handling, not 2x over-fetch
      const bufferSize = Math.min(5, Math.ceil(end * 0.2)); // 20% buffer max 5 docs
      const optimizedLimit = fetchLimit + bufferSize;

      console.log(`FirebaseService.fetchDataInRange: Optimized query - requested: ${end}, limit: ${optimizedLimit} (reduced from ${fetchLimit * 2})`);

      let initialDocs;
      try {
        // Try with composite index (isSubscribed DESC, creationTime DESC)
        const query: Query = firestoreQuery(
          collection(db, collectionName),
          orderBy("isSubscribed", "desc"), // Premium users first
          orderBy("creationTime", "desc"), // Then by recency
          firestoreLimit(optimizedLimit) // Optimized limit instead of fetchLimit * 2
        );
        initialDocs = await getDocs(query);
      } catch (indexError) {
        // Fallback: If composite index fails, use simple creationTime ordering
        // This handles cases where isSubscribed field may not exist on all documents
        console.warn('Composite index query failed, falling back to creationTime ordering:', indexError);
        const fallbackQuery: Query = firestoreQuery(
          collection(db, collectionName),
          orderBy("creationTime", "desc"),
          firestoreLimit(optimizedLimit)
        );
        initialDocs = await getDocs(fallbackQuery);
      }

      const documents = initialDocs.docs;

      if (documents.length === 0) {
        return []; // No documents found
      }

      if (documents.length < start) {
        return []; // Fewer documents than the start index
      }

      // Convert documents to objects and ensure we don't have duplicates
      const uniqueDocsMap = new Map();
      documents.forEach((doc) => {
        const data = { ...doc.data(), id: doc.id } as T;
        // Use a unique identifier from the data - typically userId or id
        const uniqueId = (data as any).userId || doc.id;
        uniqueDocsMap.set(uniqueId, data);
      });
      
      const data = Array.from(uniqueDocsMap.values());

      // Slice the data based on start and end indices
      const sliceStart = start - 1; // Convert to zero-based index
      const sliceEnd = Math.min(data.length, end);

      // Return the sliced data
      return data.slice(sliceStart, sliceEnd);
    } catch (error) {
      return {
        message: getFriendlyErrorMessage(error),
        type: "error" as const,
      };
    }
  }

  static async createOrUpdateSubDoc(
    collectionPath: string,
    docId: string,
    subCollectionPath: string,
    subDocId: string,
    data: DocumentData
  ): Promise<void | { message: string; type: "error" }> {
    try {
      const subDocRef = doc(
        db,
        collectionPath,
        docId,
        subCollectionPath,
        subDocId
      );
      const docSnap = await getDoc(subDocRef);

      if (docSnap.exists()) {
        await updateDoc(subDocRef, data);
      } else {
        await setDoc(subDocRef, data);
      }
    } catch (error) {
      return {
        message: getFriendlyErrorMessage(error),
        type: "error" as const,
      };
    }
  }

  static async getActiveSubscription(userId: string) {
    try {
      // First, try to fetch by userId as docId (for per-user doc structure)
      const docRef = doc(db, 'subscriptions', userId);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        const data = docSnap.data();
        if (data.status === 'active') {
          return data;
        }
      }
      // Fallback: query by userId and status (for per-subscription doc structure)
      const subscriptionsRef = collection(db, 'subscriptions');
      const q = firestoreQuery(
        subscriptionsRef,
        where('userId', '==', userId),
        where('status', '==', 'active'),
        firestoreLimit(1)
      );
      const querySnapshot = await getDocs(q);
      if (!querySnapshot.empty) {
        return querySnapshot.docs[0].data();
      }
      return null;
    } catch (error) {
      return {
        message: getFriendlyErrorMessage(error),
        type: 'error' as const,
      };
    }
  }

  static async sendPasswordReset(email: string) {
    try {
      await sendPasswordResetEmail(auth, email);
      return { type: "success" as const };
    } catch (error) {
      return {
        message: getFriendlyErrorMessage(error),
        type: "error" as const,
      };
    }
  }
}
